import React, { lazy, Suspense, useMemo, useEffect } from 'react'
import { LeftOutlined } from '@ant-design/icons'
import { UserAvatar } from '@forwardflow/ui/Avatar'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import relativeTime from 'dayjs/plugin/relativeTime'

dayjs.extend(duration)
dayjs.extend(relativeTime)

import { Avatar, Skeleton, Tooltip } from 'antd'
import Icon from '@forwardflow/ui/Icons'
import {
   formatRelativeTime,
   getAvatarColor,
   getPlatformType,
} from '@forwardflow/helpers/utils'
import { formatNumber } from '@forwardflow/helpers/utils/formatNumber'
import { useDemoConversationStore } from '@forwardflow/store/demoConversationStore'
import { getCampaignTypeDisplayName, getDefaultValues, getStageDisplayName } from '@forwardflow/helpers/utils/demoConversation'

const LeadSummarySection = lazy(() => import('../../LeadDetails/LeadSummarySection'))

interface DemoLeadDetailsProps {
   leadDetails: any
}

const DemoLeadDetails: React.FC<DemoLeadDetailsProps> = ({ leadDetails }) => {
   const leadId = leadDetails?.userInfo?.id;

   const storedLeadDetails = useDemoConversationStore((state) =>
      leadId ? state.getLeadDetails(leadId) : null
   )

   const timelineHistory = useDemoConversationStore((state) =>
      leadId ? state.getTimelineHistory(leadId) : []
   )

   const effectiveStage = useDemoConversationStore((state) =>
      leadId ? state.getEffectiveStage(leadId) : null
   )

   const updateStage = useDemoConversationStore((state) => state.updateStage)

   const finalLeadDetails = useMemo(() => {
      if (!storedLeadDetails) {
         return leadDetails
      }

      const defaults = getDefaultValues()
      let latestStage = storedLeadDetails.stageInfo
      let calculatedStageName = ''
      let latestAppointmentInfo = storedLeadDetails.appointmentInfo
      let timelineSummary = storedLeadDetails.timelineSummary
      let questions = storedLeadDetails.questions

      if (timelineHistory.length === 0) {
         calculatedStageName = effectiveStage || storedLeadDetails.stageInfo?.name

         latestStage = {
            id: 1,
            name: calculatedStageName
         }

         latestAppointmentInfo = defaults.appointmentInfo
         timelineSummary = defaults.timelineSummary
         questions = defaults.questions

      } else {
         const lastStageItem = timelineHistory
            .slice()
            .reverse()
            .find(item => item.campaignPhase)

         if (lastStageItem) {
            if (lastStageItem.itemType === 'post_analysis') {
               calculatedStageName = lastStageItem.campaignPhase || storedLeadDetails.stageInfo?.name || ''
            } else if (lastStageItem.itemType === 'appointment') {
               calculatedStageName = lastStageItem.campaignPhase || 'ff-appointment-set'
            } else if (lastStageItem.itemType === 'init_summary' && lastStageItem.campaignPhase) {
               calculatedStageName = lastStageItem.campaignPhase || 'ff-lead'
            } else if (lastStageItem.campaignPhase) {
               calculatedStageName = lastStageItem.campaignPhase
            }

            latestStage = {
               ...storedLeadDetails.stageInfo,
               name: calculatedStageName,
               ...(lastStageItem.ts && { updatedAt: lastStageItem.ts })
            }
         } else {
            calculatedStageName = effectiveStage || storedLeadDetails.stageInfo?.name
            latestStage = {
               ...storedLeadDetails.stageInfo,
               name: calculatedStageName
            }
         }

         const lastAppointmentItem = timelineHistory
            .slice()
            .reverse()
            .find(item => item.itemType === 'appointment')

         if (lastAppointmentItem?.appointment) {
            latestAppointmentInfo = {
               date: lastAppointmentItem.appointment.date,
               status: lastAppointmentItem.appointment.status || 'scheduled',
               platform: lastAppointmentItem.appointment.platform || 'Other'
            }
         } else {
            latestAppointmentInfo = defaults.appointmentInfo
         }

         const lastTimelineSummaryItem = timelineHistory
            .slice()
            .reverse()
            .find(item => item.timelineSummary)

         if (lastTimelineSummaryItem?.timelineSummary) {
            timelineSummary = lastTimelineSummaryItem.timelineSummary
         } else {
            timelineSummary = storedLeadDetails.timelineSummary || defaults.timelineSummary
         }

         const lastQuestionsItem = timelineHistory
            .slice()
            .reverse()
            .find(item => item.questions)

         if (lastQuestionsItem?.questions) {
            questions = lastQuestionsItem.questions
         } else {
            questions = storedLeadDetails.questions || defaults.questions
         }
      }

      return {
         ...leadDetails,
         conversationInfo: storedLeadDetails.conversationInfo,
         appointmentInfo: latestAppointmentInfo,
         stageInfo: latestStage,
         tags: storedLeadDetails.tags,
         leadSummary: timelineSummary,
         questions: questions,
         userInfo: leadDetails.userInfo,
         agentInfo: leadDetails.agentInfo,
         sourceInfo: leadDetails.sourceInfo,
         propertyInfo: leadDetails.propertyInfo,
      }
   }, [storedLeadDetails, timelineHistory, leadDetails, effectiveStage])

   useEffect(() => {
      if (leadId && storedLeadDetails && finalLeadDetails.stageInfo.name) {
         const currentStoredStageName = storedLeadDetails.stageInfo?.name
         const calculatedStageName = finalLeadDetails.stageInfo.name

         if (currentStoredStageName !== calculatedStageName && calculatedStageName) {
            updateStage(
               leadId,
               finalLeadDetails.stageInfo.id || storedLeadDetails.stageInfo?.id || 1,
               calculatedStageName
            )
         }
      }
   }, [leadId, finalLeadDetails.stageInfo.name, storedLeadDetails?.stageInfo?.name, updateStage, timelineHistory.length])

   return (
      <div
         className="bg-primary/10 p-6 lg:w-[360px] overflow-y-auto shrink-0"
         data-testid="demo-lead-details-container"
      >
         <div className="flex items-center justify-between pb-3 mb-6 border-b border-white/10">
            <div className="flex items-center">
               <UserAvatar
                  className="!text-[14px] font-medium mr-3 !w-10 !h-10"
                  firstName={finalLeadDetails?.userInfo.firstName}
                  backgroundColor={
                     getAvatarColor(finalLeadDetails?.stageInfo?.name) || undefined
                  }
               />
               <div className="truncate">
                  {`${finalLeadDetails?.userInfo.firstName} ${finalLeadDetails?.userInfo.lastName}`}
               </div>
            </div>
            <button className="hidden p-2 bg-gray-700 rounded-full shrink-0">
               <LeftOutlined />
            </button>
         </div>

         <div className="pb-5 mb-5 space-y-3 text-sm border-b border-white/10 text-white/80">
            <div className="flex items-center">
               <Icon name="Call" className="mr-3 fill-[#7E7E7E] scale-105" />
               <span
                  className={`${finalLeadDetails?.userInfo.phone
                     ? 'text-white'
                     : 'text-[#7E7E7E]'
                     }`}
               >
                  {finalLeadDetails?.userInfo.phone}
               </span>
            </div>
            <div className="flex items-center">
               <Icon name="Email" className="mr-3 fill-[#7E7E7E] scale-105" />
               <span
                  className={`${finalLeadDetails?.userInfo.email
                     ? 'text-white'
                     : 'text-[#7E7E7E]'
                     }`}
               >
                  {finalLeadDetails?.userInfo.email || 'No email found'}
               </span>
            </div>
            <div className="flex items-center">
               <Icon name="Home" className="mr-3 fill-[#7E7E7E]" />
               <span
                  className={`${finalLeadDetails?.propertyInfo ? 'text-white' : 'text-[#7E7E7E]'
                     }`}
               >
                  {finalLeadDetails?.propertyInfo || 'No address found'}
               </span>
            </div>
         </div>

         {/* Lead Summary Section */}
         {finalLeadDetails?.leadSummary && (
            <Suspense fallback={<Skeleton active paragraph={{ rows: 2 }} />}>
               <LeadSummarySection summary={finalLeadDetails.leadSummary} />
            </Suspense>
         )}

         {/* Questions Section */}
         {finalLeadDetails?.questions && (
            <div className="pb-5 mb-5 space-y-4 border-b border-white/10">
               <h3 className="mb-2 text-[14px] font-medium">
                  Questions from Lead
               </h3>
               <p className="text-white/70 text-[12px]">
                  {finalLeadDetails?.questions || 'No questions provided.'}
               </p>
            </div>
         )}

         <div className="pb-5 mb-5 text-sm border-b border-white/10">
            <h3 className="mb-3 text-[14px]">Appointment Status</h3>
            <div className="flex items-center justify-between px-3 py-2 pr-0 rounded-lg bg-black/20">
               <div className="flex flex-col text-[12px] w-full">
                  <div>
                     {!finalLeadDetails?.appointmentInfo?.date ? (
                        <div className="text-white/70">Not yet scheduled</div>
                     ) : (
                        <div className="flex items-center">
                           <div className="pr-2 capitalize border-r border-white/20">
                              {finalLeadDetails?.appointmentInfo?.status?.split('(PLACEHOLDER)')[0] || 'Scheduled'}
                           </div>
                           <div className="px-2 text-center capitalize">
                              {finalLeadDetails?.appointmentInfo?.date && (
                                 <span>
                                    {dayjs(finalLeadDetails.appointmentInfo.date).format('h:mm A, D MMM YYYY')}
                                 </span>
                              )}
                           </div>
                           <div>
                              {finalLeadDetails?.appointmentInfo?.platform && (
                                 <div className="text-[18px] font-medium text-center border-l border-white/20 pl-2">
                                    {getPlatformType(
                                       finalLeadDetails?.appointmentInfo?.platform?.split('(PLACEHOLDER)')[0] ||
                                       finalLeadDetails?.appointmentInfo?.platform
                                    )}
                                 </div>
                              )}
                           </div>
                        </div>
                     )}
                  </div>
                  {finalLeadDetails?.appointmentInfo?.updatedAt && (
                     <div className="mt-1 text-[10px] text-white/50">
                        Updated {formatRelativeTime(finalLeadDetails.appointmentInfo.updatedAt)}
                     </div>
                  )}
               </div>
            </div>
         </div>

         {/* Agent, Stage, Source, Lead Type, Tags Section */}
         <div className="pb-5 mb-5 space-y-4 text-sm border-b border-white/10">
            <div className="flex items-center gap-4">
               <span>Agent</span>
               <div className="pr-5 rounded-full flexc bg-white/10">
                  <p className="text-[12px] font-medium pr-2">
                     <Avatar
                        src={finalLeadDetails?.agentInfo.avatar}
                        className="w-9 h-9 bg-[#851E58]"
                     >
                        {finalLeadDetails?.agentInfo?.name?.slice(0, 1) || 'A'}
                     </Avatar>
                  </p>
                  <p className="text-[12px] font-medium">
                     {`${finalLeadDetails?.agentInfo?.name}`}
                  </p>
               </div>
            </div>

            <div className="flex items-center gap-4">
               <span>Stage</span>
               <div className="flex items-center">
                  <Tooltip title={getStageDisplayName(finalLeadDetails?.stageInfo?.name)}>
                     <span className="px-3 py-1 mr-4 truncate bg-purple-600 rounded-full">
                        {getStageDisplayName(finalLeadDetails?.stageInfo?.name) || 'None'}
                     </span>
                  </Tooltip>
                  <span className="text-gray-400">
                     {formatRelativeTime(finalLeadDetails?.stageInfo?.updatedAt)}
                  </span>
               </div>
            </div>

            <div className="flex items-center gap-4">
               <span>Source</span>
               <div className="flex items-center">
                  <span className="px-3 py-1 mr-4 rounded-full bg-white/10">
                     {finalLeadDetails?.sourceInfo?.name || 'Demo Source'}
                  </span>
               </div>
            </div>

            <div className="flex items-center gap-4">
               <span>Lead Type</span>
               <span className="px-3 py-1 mr-4 rounded-full bg-white/10">
                  {getCampaignTypeDisplayName(finalLeadDetails?.leadInterestType) || 'None'}
               </span>
            </div>

            <div className="flex items-center gap-4">
               <span className="mr-3">Tags</span>
               <div className="flex items-center gap-2 pb-1 overflow-x-auto whitespace-nowrap">
                  {finalLeadDetails?.tags?.length > 0 ? (
                     finalLeadDetails.tags.map((tag: any, index: number) => (
                        <span
                           key={index}
                           className="px-3 py-1 rounded-full bg-white/10 shrink-0"
                        >
                           {typeof tag === 'string' ? tag : tag?.name || tag}
                        </span>
                     ))
                  ) : (
                     <span className="text-right text-gray-400">
                        No Tags Found
                     </span>
                  )}
               </div>
            </div>
         </div>

         {/* Conversation Statistics */}
         <div className="flex flex-col gap-4 pb-6 mb-6 text-sm">
            <div className="flex items-center gap-x-7">
               <div className="flex items-center justify-between flex-1">
                  <div>Msg sent</div>
                  <div>
                     {formatNumber(finalLeadDetails?.conversationInfo?.messagesSent || 0)}
                  </div>
               </div>
               <div className="flex items-center justify-between flex-1">
                  <div>Msg replied</div>
                  <div>
                     {formatNumber(finalLeadDetails?.conversationInfo?.messagesReplied || 0)}
                  </div>
               </div>
            </div>

            <div className="flex items-center gap-x-6">
               <div className="flex items-center justify-between flex-1">
                  <div>Calls attempted</div>
                  <div>
                     {formatNumber(finalLeadDetails?.conversationInfo?.callsAttempted || 0)}
                  </div>
               </div>
               <div className="flex items-center justify-between flex-1">
                  <div>Calls answered</div>
                  <div>
                     {formatNumber(finalLeadDetails?.conversationInfo?.callsAnswered || 0)}
                  </div>
               </div>
            </div>
         </div>
      </div>
   )
}

export default DemoLeadDetails